import { DEFAULT_DATE_FORMAT_FNS, useGetTimetableStructuresQuery } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format, startOfToday } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form-lts';
import { useParams } from 'react-router-dom';

import { useRouter } from '../../../context/router/useRouter';
import useSchoolYears from '../../../hooks/useSchoolYears';
import { RegisterCreateForm, useRegisterComposition } from './useRegisterComposition';
import { useRegisterDateStep } from './useRegisterDateStep';
import { useRegisterGroupSelection } from './useRegisterGroupSelection';
import { useRegisterSubmit } from './useRegisterSubmit';
import { RegisterTab } from './types';
import { emptyGroupIds } from './utils/registerGroupIds';
import {
  emptyRegisterStructuresWithPeriods,
  getRegisterStructuresFromComposition,
  getRegisterStructuresWithPeriods,
} from './utils/registerPeriods';

export type RegisterCreateStep = 'date' | 'groups';

export const useRegisterCreateModal = () => {
  const { goBack } = useRouter();
  const { schoolId = '' } = useAuth();
  const { id: registerId } = useParams<{ id?: string }>();
  const isEdit = !!registerId;
  const { getConfirmation } = useConfirmationDialog();

  const [step, setStep] = useState<RegisterCreateStep>(isEdit ? 'groups' : 'date');
  const [hasManualSelection, setHasManualSelection] = useState(false);
  const [selectedTab, setSelectedTab] = useState<RegisterTab | undefined>();

  const today = useMemo(() => startOfToday(), []);

  const form = useForm<RegisterCreateForm>({
    defaultValues: {
      register_date: format(today, DEFAULT_DATE_FORMAT_FNS),
      group_ids: emptyGroupIds(),
    },
  });

  const { composition, compositionLoading, initialRosterIds, isSeeded } = useRegisterComposition({
    form,
    registerId,
  });

  const dateStep = useRegisterDateStep({
    form,
    isEdit,
    setStep,
    setHasManualSelection,
    setSelectedTab,
  });

  const { getSchoolYearByDate, isLoading: schoolYearsLoading } = useSchoolYears();
  const registerDate = useMemo(() => newDateTimezoneOffset(dateStep.date), [dateStep.date]);
  const schoolYearId = getSchoolYearByDate(registerDate)?.id;

  const { data: timetableStructuresData, isLoading: timetableStructuresLoading } =
    useGetTimetableStructuresQuery(
      { school_id: schoolId },
      { enabled: !!schoolId && !isEdit, refetchOnMount: 'always' },
    );

  const { structures: timetableStructures, periodsByStructureId } = useMemo(() => {
    if (isEdit) {
      return getRegisterStructuresFromComposition(composition?.periods ?? []);
    }

    if (!schoolYearId) return emptyRegisterStructuresWithPeriods();

    return getRegisterStructuresWithPeriods(
      timetableStructuresData?.results ?? [],
      registerDate,
      schoolYearId,
    );
  }, [composition?.periods, isEdit, registerDate, schoolYearId, timetableStructuresData?.results]);

  const periods = useMemo(() => Object.values(periodsByStructureId).flat(), [periodsByStructureId]);

  const groupSelection = useRegisterGroupSelection({
    form,
    isEdit,
    step,
    date: dateStep.date,
    composition,
    isSeeded,
    hasManualSelection,
    setHasManualSelection,
    selectedTab,
    periods,
  });

  const { submit, remove, saving, deleting } = useRegisterSubmit({
    form,
    isEdit,
    registerId,
    date: dateStep.date,
    composition,
    eligibleGroups: groupSelection.eligibleGroups,
    hasManualSelection,
    initialRosterIds,
    periods,
  });

  const close = useCallback(async () => {
    if (!form.formState.isDirty) {
      goBack();
      return;
    }

    if (await getConfirmation({ textId: 'attendance-CloseModalWarning' })) {
      goBack();
    }
  }, [form.formState.isDirty, getConfirmation, goBack]);

  return {
    form,
    step,
    isEdit,
    registerId,
    activeTab: groupSelection.activeTab,
    switchTab: setSelectedTab,
    date: dateStep.date,
    minDate: dateStep.minDate,
    maxDate: dateStep.maxDate,
    eligibleGroups: groupSelection.eligibleGroups,
    timetableStructures,
    periodsByStructureId,
    timetableStructuresLoading: isEdit ? false : timetableStructuresLoading || schoolYearsLoading,
    compositionLoading,
    eligibleGroupsLoading: isSeeded && groupSelection.eligibleGroupsLoading,
    eligibleGroupsFetching: groupSelection.eligibleGroupsFetching,
    availableTabs: groupSelection.availableTabs,
    addedGroups: groupSelection.addedGroups,
    groupIds: groupSelection.selectedGroupIds,
    initialRosterIds,
    saving,
    deleting,
    checkingDate: dateStep.checkingDate,
    errors: dateStep.errors,
    actions: {
      onDateChange: dateStep.onDateChange,
      goToGroups: dateStep.goToGroups,
      goToDate: dateStep.goToDate,
      addGroupId: groupSelection.addGroupId,
      removeGroupId: groupSelection.removeGroupId,
      submit,
      remove,
      close,
    },
  };
};
